import React from 'react';

interface AutoRefreshOptions {
  /* 0 means auto-refresh is off */
  intervalMinutes: number;
  /* When the data was last successfully (re)loaded, as an ISO string. Both the
     timer cadence and the catch-up below are measured from it. */
  lastRefreshedAt: string | null;
  /* There is something to refresh: the initial load completed and did not fail */
  enabled: boolean;
  onRefresh: () => void;
}

/* Refreshing content on a per-user configurable interval while it stays on
 * screen.
 *
 * A plain setInterval, which a background tab may throttle, so a
 * visibilitychange listener catches up when the tab becomes visible again and
 * the last refresh is older than the interval — instead of waiting for the
 * (possibly long delayed) next tick.
 *
 * The timer is re-armed whenever lastRefreshedAt moves, which is what makes a
 * manual refresh reset the cadence. A *failed* refresh deliberately does not
 * re-arm it: lastRefreshedAt only moves on success. Keep the dependencies
 * exactly as they are — adding the in-flight or failure state would change
 * that cadence. */
const useAutoRefresh = ({
  intervalMinutes,
  lastRefreshedAt,
  enabled,
  onRefresh
}: AutoRefreshOptions) => {
  /* Held in a ref so a new callback identity on every render does not restart
     the timer */
  const onRefreshRef = React.useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  React.useEffect(() => {
    if (!enabled || intervalMinutes === 0) return undefined;
    const intervalMilliseconds = intervalMinutes * 60 * 1000;

    const timer = window.setInterval(
      () => onRefreshRef.current(),
      intervalMilliseconds
    );

    const catchUpWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const lastRefreshed = lastRefreshedAt ? Date.parse(lastRefreshedAt) : 0;
      if (Date.now() - lastRefreshed >= intervalMilliseconds) onRefreshRef.current();
    };
    document.addEventListener('visibilitychange', catchUpWhenVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', catchUpWhenVisible);
    };
  }, [intervalMinutes, lastRefreshedAt, enabled]);
};

export default useAutoRefresh;
